import {
  PHYS_SCALE,
  FIXED_TIMESTEP,
  POSITION_ITERATIONS,
  VELOCITY_ITERATIONS,
  b2AABB,
  b2Body,
  b2BodyDef,
  b2CircleShape,
  b2DistanceJointDef,
  b2FixtureDef,
  b2PolygonShape,
  b2PrismaticJointDef,
  b2RevoluteJointDef,
  b2Vec2,
  b2World,
} from "./box2d-compat.js";
import { PlayerBox } from "./player-box.js";
import { MyContactListener } from "./contact-listener.js";
import { displayBounds } from "./flash-display-bounds.js";

const LEFT_KEYS = ["ArrowLeft", "KeyA"];
const RIGHT_KEYS = ["ArrowRight", "KeyD"];
const UP_KEYS = ["ArrowUp", "KeyW", "Space"];
const CAMERA_EASE = 1 - Math.pow(2, -10 / 31);
const CHECKPOINT_FRAME_COUNT = 5;
const LEVEL_AIM_FRAME_COUNT = 20;
const DEATH_DELAY_FRAMES = Math.round(1200 / (1000 / 31));
const STOCK_WIN_DELAY_FRAMES = 88;
const FIXED_WIN_DELAY_FRAMES = 89;
const DISPLAY_HAZARD_SYMBOL_IDS = new Set([57, 58]);
const DEATH_WARP_LEVEL_IDS = new Set([4, 9]);

function snapToFlashTwips(value) {
  return Math.trunc(value * 20) / 20;
}

function rectsOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function sortedFramePaths(folder, frameCount) {
  return Array.from({ length: frameCount }, (_, index) => `${folder}/${index + 1}.png`);
}

function playerFlagBounds(player) {
  const size = player.radius * 2;
  const halfExtent = size / 2 * (Math.abs(Math.cos(player.body.GetAngle())) + Math.abs(Math.sin(player.body.GetAngle())));
  return {
    x: player.x - halfExtent,
    y: player.y - halfExtent,
    width: halfExtent * 2,
    height: halfExtent * 2,
  };
}

export class FlashLevelBaseRuntime {
  static levelFlags = {
    7: { redCheckLevel: false },
    13: { greenCheckLevel: false },
    16: { isStrelka: false },
  };

  constructor(levelData, clock, runtimeState = null, { audioEnabled = true, assetsEnabled = true, fixedLevelTransitionTiming = false, tasDeathWarp = false } = {}) {
    this.levelData = levelData;
    this.levelId = levelData.id;
    this.clock = clock;
    this.audioEnabled = audioEnabled;
    this.assetsEnabled = assetsEnabled;
    this.fixedLevelTransitionTiming = fixedLevelTransitionTiming === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;
    this.tasDeathWarp = tasDeathWarp === true;
    this.world = new b2World(new b2Vec2(0, 10), true);
    this.ground = this.world.GetGroundBody();
    this.contactListener = new MyContactListener();
    this.world.SetContactListener(this.contactListener);

    this.complete = false;
    this.failed = false;
    this.winActive = false;
    this.deathCounter = -1;
    this.winCounter = -1;
    this.spikesKill = false;
    this.camera = { x: 0, y: 0 };
    this.namedBodies = {};
    this.staticBodies = [];
    this.dynamicBodies = {};
    this.namedObjectStates = {};
    this.particles = [];
    this.namedRects = {};
    this.levelState = {};
    this.lastCheckNum = runtimeState?.checkpointIndex ?? 0;
    this.deathWarpPlayerRect = null;
    this.deathWarpPlayerStageRect = null;

    this.stageImage = this.assetsEnabled ? new Image() : null;
    if (this.stageImage) {
      this.stageImage.src = levelData.imagePath ?? levelData.foregroundSvgPath ?? levelData.svgPath;
    }

    this.playerImage = this.assetsEnabled ? new Image() : null;
    if (this.playerImage) {
      this.playerImage.src = "scratch/l1_png/DefineSprite_23_PlayerBox/1.png";
    }

    this.backgroundImage = (this.assetsEnabled && levelData.symbols?.backGroundMC?.imagePath) ? new Image() : null;
    if (this.backgroundImage) {
      this.backgroundImage.src = levelData.symbols.backGroundMC.imagePath;
    }
    this.backgroundStart = levelData.layout?.namedChildren?.backGroundMC ?? null;
    this.backgroundSymbol = levelData.symbols?.backGroundMC ?? null;

    this.goalFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_68", LEVEL_AIM_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;

    this.checkpointFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_85", CHECKPOINT_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];

    this.refreshNamedRects();
    this.checkpoints = Object.entries(this.namedRects)
      .filter(([name]) => /^checkPoint\d+$/.test(name))
      .sort((a, b) => Number(a[0].replace("checkPoint", "")) - Number(b[0].replace("checkPoint", "")))
      .map(([name, rect], index) => {
        const layout = this.levelData.layout.namedChildren[name] ?? rect;
        return {
          name,
          index,
          rect,
          x: layout.x,
          y: layout.y,
          currentFrame: index < this.lastCheckNum ? 5 : 1,
          playing: false,
        };
      });
    this.goalRect = this.namedRects.levelAim ?? { x: 0, y: 0, width: 20, height: 20 };
    this.goalPosition = this.levelData.layout.namedChildren.levelAim ?? { x: 0, y: 0 };
    this.goalSymbol = this.levelData.symbols?.levelAim ?? null;
    this.hazardNames = Object.keys(this.levelData.layout.namedChildren).filter((name) => {
      const symbolId = this.levelData.symbols?.[name]?.symbolId;
      return DISPLAY_HAZARD_SYMBOL_IDS.has(symbolId);
    });

    // Flash Level constructs the player body in the base constructor before
    // level-specific bodies are created in the subclass constructor.
    this.resetPlayer(runtimeState);
    this.buildLevel();
    this.refreshNamedRects();
  }

  setSpikesKill(enabled) {
    this.spikesKill = enabled;
  }

  setFixedLevelTransitionTiming(enabled) {
    this.fixedLevelTransitionTiming = enabled === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;
  }

  setTasDeathWarp(enabled) {
    this.tasDeathWarp = enabled === true;
  }

  isTasDeathWarpEnabled() {
    return this.tasDeathWarp && DEATH_WARP_LEVEL_IDS.has(this.levelId);
  }

  serializeState() {
    return {
      checkpointIndex: this.lastCheckNum,
      levelFlags: FlashLevelBaseRuntime.levelFlags,
    };
  }

  getBody(name) {
    return this.namedBodies[name] ?? null;
  }

  getDisplayLayout(name) {
    const layout = this.levelData.layout.namedChildren[name];
    if (!layout) {
      return null;
    }
    const body = this.getBody(name);
    if (!body || body.GetType() === b2Body.b2_staticBody) {
      return layout;
    }
    const origin = body.GetWorldPoint(new b2Vec2(0, 0));
    return {
      ...layout,
      x: snapToFlashTwips(origin.x * PHYS_SCALE),
      y: snapToFlashTwips(origin.y * PHYS_SCALE),
      rotation: body.GetAngle() * (180 / Math.PI),
    };
  }

  refreshNamedRects() {
    this.namedRects = Object.fromEntries(
      Object.keys(this.levelData.layout.namedChildren).map((name) => {
        const layout = this.getDisplayLayout(name);
        return [name, displayBounds(layout, this.levelData.symbols?.[name], name) ?? layout];
      }),
    );
    if (this.namedRects.levelAim) {
      this.goalRect = this.namedRects.levelAim;
    }
  }

  getNamedObjectFrame(name) {
    return this.namedObjectStates[name]?.currentFrame ?? 1;
  }

  setNamedObjectFrame(name, currentFrame) {
    if (!this.namedObjectStates[name]) {
      this.namedObjectStates[name] = { currentFrame: 1 };
    }
    this.namedObjectStates[name].currentFrame = currentFrame;
  }

  createBody(name, shape, density, friction, restitution, data) {
    const layout = this.levelData.layout.namedChildren[name];
    if (!layout) {
      throw new Error(`Missing layout for body '${name}'`);
    }
    const bodyDef = new b2BodyDef();
    bodyDef.position.Set(layout.x / PHYS_SCALE, layout.y / PHYS_SCALE);
    bodyDef.angle = ((layout.rotation ?? 0) * Math.PI) / 180;
    bodyDef.userData = { __kind: "body", name };
    const body = this.world.CreateBody(bodyDef);
    if (shape === "Circle") {
      const fixtureDef = new b2FixtureDef();
      fixtureDef.density = density;
      fixtureDef.friction = friction;
      fixtureDef.restitution = restitution;
      fixtureDef.shape = new b2CircleShape((data[0] / 2) / PHYS_SCALE);
      fixtureDef.shape.SetLocalPosition(new b2Vec2((data[0] / 2) / PHYS_SCALE, (data[0] / 2) / PHYS_SCALE));
      body.CreateFixture(fixtureDef);
    } else {
      for (const polygon of data) {
        const fixtureDef = new b2FixtureDef();
        fixtureDef.density = density;
        fixtureDef.friction = friction;
        fixtureDef.restitution = restitution;
        fixtureDef.shape = new b2PolygonShape();
        fixtureDef.shape.SetAsArray(polygon.map(([x, y]) => new b2Vec2(x / PHYS_SCALE, y / PHYS_SCALE)), polygon.length);
        body.CreateFixture(fixtureDef);
      }
    }
    body.SetMassFromShapes();
    this.namedBodies[name] = body;
    this.dynamicBodies[name] = body;
    if (density === 0) {
      this.staticBodies.push(body);
    } else {
      body.SetAwake(true);
    }
    return body;
  }

  createBodyFromLevelData(name, overrides = {}) {
    const entry = this.levelData.bodies.find((candidate) => candidate.name === name);
    if (!entry) {
      throw new Error(`Missing generated body '${name}' for level ${this.levelId}`);
    }
    return this.createBody(
      name,
      entry.shape,
      overrides.density ?? entry.density,
      overrides.friction ?? entry.friction,
      overrides.restitution ?? entry.restitution,
      overrides.data ?? entry.data,
    );
  }

  createAllLevelBodies() {
    for (const entry of this.levelData.bodies) {
      this.createBodyFromLevelData(entry.name);
    }
  }

  applyBodyGroupIndex(body, groupIndex) {
    let fixture = body?.GetFixtureList?.() ?? null;
    while (fixture) {
      const filter = fixture.GetFilterData();
      filter.groupIndex = groupIndex;
      fixture.SetFilterData(filter);
      this.world.Refilter(fixture);
      fixture = fixture.GetNext?.() ?? fixture.m_next ?? null;
    }
  }

  destroyBody(body) {
    if (!body) {
      return;
    }
    const name = body.GetUserData()?.name;
    this.world.DestroyBody(body);
    if (name) {
      delete this.namedBodies[name];
      delete this.dynamicBodies[name];
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    // Background (parallax)
    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    // Foreground stage image
    if (this.stageImage?.complete) {
      const { layout, pngLayout } = this.levelData;
      if (pngLayout) {
        ctx.drawImage(
          this.stageImage,
          pngLayout.cropX, pngLayout.cropY,
          pngLayout.cropWidth, pngLayout.cropHeight,
          layout.imageOffsetX, layout.imageOffsetY,
          layout.imageWidth, layout.imageHeight,
        );
      } else {
        ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY);
      }
    }

    // Checkpoints
    for (const checkpointState of this.checkpoints) {
      const frame = this.checkpointFrames[checkpointState.currentFrame - 1];
      const sym = this.levelData.symbols?.[checkpointState.name];
      if (frame?.complete && sym?.layout) {
        ctx.save();
        ctx.translate(checkpointState.x, checkpointState.y);
        ctx.drawImage(frame, sym.layout.imageOffsetX, sym.layout.imageOffsetY, sym.layout.imageWidth, sym.layout.imageHeight);
        ctx.restore();
      }
    }

    // Goal
    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    const goalSym = this.goalSymbol;
    if (goalFrame?.complete && goalSym?.layout) {
      ctx.save();
      ctx.translate(this.goalPosition.x, this.goalPosition.y);
      ctx.drawImage(goalFrame, goalSym.layout.imageOffsetX, goalSym.layout.imageOffsetY, goalSym.layout.imageWidth, goalSym.layout.imageHeight);
      ctx.restore();
    }

    // Player
    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    // Death particles
    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  buildLevel() {}

  addPrismaticJoint(bodyA, bodyB, anchor, axis, options = {}) {
    const jointDef = new b2PrismaticJointDef();
    jointDef.Initialize(bodyA, bodyB, anchor, axis);
    Object.assign(jointDef, options);
    return this.world.CreateJoint(jointDef);
  }

  addRevoluteJoint(bodyA, bodyB, anchor, options = {}) {
    const jointDef = new b2RevoluteJointDef();
    jointDef.Initialize(bodyA, bodyB, anchor);
    Object.assign(jointDef, options);
    return this.world.CreateJoint(jointDef);
  }

  addDistanceJoint(bodyA, bodyB, anchorA, anchorB, options = {}) {
    const jointDef = new b2DistanceJointDef();
    jointDef.Initialize(bodyA, bodyB, anchorA, anchorB);
    Object.assign(jointDef, options);
    return this.world.CreateJoint(jointDef);
  }

  resetPlayer(runtimeState = null) {
    if (this.player?.alive) {
      this.player.kill(this.world);
    }
    const checkpoint = this.checkpoints[this.lastCheckNum] ?? this.checkpoints[0] ?? { x: 0, y: 0 };
    this.player = new PlayerBox(this.world, checkpoint.x, checkpoint.y);
    this.player.body.SetUserData({ __kind: "player", ref: this.player });
    this.particles = [];
    this.deathCounter = -1;
    this.failed = false;
    this.complete = false;
    this.winActive = false;
    this.deathWarpPlayerRect = null;
    this.deathWarpPlayerStageRect = null;
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;
    this.winCounter = -1;
    this.camera.x = -checkpoint.x + 275;
    this.camera.y = -checkpoint.y + 200;
    for (const checkpointState of this.checkpoints) {
      checkpointState.currentFrame = checkpointState.index < this.lastCheckNum ? 5 : 1;
      checkpointState.playing = false;
    }
    if (!runtimeState) {
      this.clock?.reset?.();
    }
  }

  getBodyAtPoint(x, y, includeStatic = false) {
    const point = new b2Vec2(x, y);
    const aabb = new b2AABB();
    aabb.lowerBound.Set(x - 0.001, y - 0.001);
    aabb.upperBound.Set(x + 0.001, y + 0.001);
    const fixtures = [];
    const broadPhase = this.world?.m_broadPhase;
    const count = typeof broadPhase?.QueryAABB === "function"
      ? broadPhase.QueryAABB(aabb, fixtures, 10)
      : 0;
    for (let index = 0; index < count; index += 1) {
      const fixture = fixtures[index];
      const body = fixture?.GetBody?.() ?? fixture?.m_body ?? null;
      const shape = fixture?.GetShape?.() ?? fixture?.m_shape ?? fixture;
      if (!body || !shape) {
        continue;
      }
      if ((body.GetType() !== b2Body.b2_staticBody || includeStatic) && shape.TestPoint(body.GetTransform(), point)) {
        return body;
      }
    }
    return null;
  }

  getPlayerRect() {
    return playerFlagBounds(this.player);
  }

  playerOverlapsRect(rect) {
    return Boolean(rect && this.player?.isLive?.() && rectsOverlap(this.getPlayerRect(), rect));
  }

  getStageRect(rect) {
    if (!rect) {
      return null;
    }
    return {
      x: rect.x + this.camera.x,
      y: rect.y + this.camera.y,
      width: rect.width,
      height: rect.height,
    };
  }

  deathWarpOverlapsGoal() {
    return Boolean(
      this.deathWarpPlayerStageRect
      && this.goalRect
      && rectsOverlap(this.deathWarpPlayerStageRect, this.getStageRect(this.goalRect)),
    );
  }

  hitNamedObject(name) {
    const rect = this.namedRects[name];
    if (!rect || !this.player?.isLive()) {
      return false;
    }
    return this.playerOverlapsRect(rect);
  }

  playerHasGroundSupport() {
    const position = this.player.body.GetPosition();
    return Boolean(this.getBodyAtPoint(position.x, position.y + 0.4, true))
      || Boolean(this.getBodyAtPoint(position.x - 0.2, position.y + 0.38, true))
      || Boolean(this.getBodyAtPoint(position.x + 0.2, position.y + 0.38, true));
  }

  handleControls(input) {
    if (this.beginFlag && RIGHT_KEYS.some((code) => input.isDown(code))) {
      this.clock?.unpause?.();
      this.beginFlag = false;
    }
    const hasGroundSupport = this.playerHasGroundSupport();
    const velocity = this.player.body.GetLinearVelocity();
    if (LEFT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x > -5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.5, velocity.y));
        }
      } else if (velocity.x > -2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.25, velocity.y));
      }
    }
    if (RIGHT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x < 5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.5, velocity.y));
        }
      } else if (velocity.x < 2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.25, velocity.y));
      }
    }
    if (UP_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (this.contactListener.playerContactBodies.length > 0 && hasGroundSupport) {
        this.player.body.ApplyForce(new b2Vec2(0, -65), this.player.body.GetWorldCenter());
      } else if (velocity.y < 0) {
        this.player.body.ApplyForce(new b2Vec2(0, -1), this.player.body.GetWorldCenter());
      }
    }
  }

  updateGoal() {
    if (this.playerOverlapsRect(this.goalRect) && this.goalCurrentFrame === 1) {
      this.startWin();
    }
  }

  updateCheckpoints() {
    for (const checkpointState of this.checkpoints) {
      if (this.playerOverlapsRect(checkpointState.rect) && checkpointState.currentFrame === 1) {
        if (checkpointState.index > this.lastCheckNum) {
          this.lastCheckNum = checkpointState.index;
        }
        checkpointState.playing = true;
      }
      if (checkpointState.currentFrame === 5) {
        checkpointState.playing = false;
      }
    }
  }

  checkDisplayHazards() {
    if (!this.spikesKill || !this.player?.isLive()) {
      return;
    }
    for (const name of this.hazardNames) {
      const rect = this.namedRects[name];
      if (rect && this.player.hitTestObjectControlPoints(rect)) {
        this.startDeath();
        return;
      }
    }
  }

  startDeath() {
    if (!this.player?.isLive() || this.deathCounter >= 0) {
      return;
    }
    const velocity = this.player.body.GetLinearVelocity();
    if (this.isTasDeathWarpEnabled()) {
      this.deathWarpPlayerRect = this.getPlayerRect();
      // Flash level 4/9 death warp relies on hitTestObject after PlayerBox has been
      // removed from the scrolling level container. At that point the player keeps
      // its local x/y as stage-space coordinates, while the goal still inherits the
      // container transform.
      this.deathWarpPlayerStageRect = { ...this.deathWarpPlayerRect };
    }
    this.deathCounter = DEATH_DELAY_FRAMES;
    for (let index = 0; index < 8; index += 1) {
      this.particles.push({
        x: this.player.x - 4 + 5 * Math.random() - 5 * Math.random(),
        y: this.player.y - 4 + 5 * Math.random() - 5 * Math.random(),
        vx: velocity.x / 3 * PHYS_SCALE + 7 * Math.cos(index * 2 * Math.PI / 8),
        vy: velocity.y / 3 * PHYS_SCALE + 7 * Math.sin(index * 2 * Math.PI / 8),
      });
    }
    this.player.kill(this.world);
  }

  startWin() {
    if (this.goalPlaying || this.complete) {
      return;
    }
    this.goalPlaying = true;
    this.winActive = true;
    this.winCounter = this.winDelayFrames;
    if (this.isTasDeathWarpEnabled()) {
      this.deathCounter = -1;
      this.failed = false;
    }
    this.clock?.Freeze?.(this.levelId === 12 ? 311 : (this.levelId === 17 ? -1 : this.winDelayFrames));
    if (this.player?.isLive?.() && ![5, 11].includes(this.levelId)) {
      this.player.body.SetLinearDamping(3);
      this.player.body.SetAngularDamping(3);
    }
  }

  updateAnimations() {
    for (const checkpointState of this.checkpoints) {
      if (checkpointState.playing && checkpointState.currentFrame < CHECKPOINT_FRAME_COUNT) {
        checkpointState.currentFrame += 1;
      } else if (checkpointState.playing) {
        checkpointState.playing = false;
      }
    }
    if (this.goalPlaying && this.goalCurrentFrame < LEVEL_AIM_FRAME_COUNT) {
      this.goalCurrentFrame += 1;
    }
    for (const particle of this.particles) {
      particle.vy += 0.35;
      particle.x += particle.vx * FIXED_TIMESTEP;
      particle.y += particle.vy * FIXED_TIMESTEP;
    }
  }

  levelUpdate() {}

  updateCamera() {
    if (!this.player?.isLive?.()) {
      return;
    }
    const targetCameraX = -this.player.x + 275;
    const targetCameraY = -this.player.y + 200;
    this.camera.x += (targetCameraX - this.camera.x) * CAMERA_EASE;
    this.camera.y += (targetCameraY - this.camera.y) * CAMERA_EASE;
  }

  update(input) {
    this.updateAnimations();
    if (this.deathCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.refreshNamedRects();
      this.levelUpdate();
      this.updateCamera();
      this.clock?.update();
      this.deathCounter -= 1;
      if (this.deathCounter <= 0) {
        this.failed = true;
      }
      return;
    }
    if (this.winCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.refreshNamedRects();
      this.levelUpdate();
      this.refreshNamedRects();
      this.updateCamera();
      this.clock?.update();
      this.winCounter -= 1;
      if (this.winCounter <= 0) {
        this.complete = true;
        this.winActive = false;
      }
      return;
    }
    this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
    this.refreshNamedRects();
    this.handleControls(input);
    this.updateGoal();
    this.checkDisplayHazards();
    this.updateCheckpoints();
    this.levelUpdate();
    this.refreshNamedRects();
    this.updateCamera();
    if (this.isTasDeathWarpEnabled() && this.goalCurrentFrame === 1 && this.deathWarpOverlapsGoal()) {
      this.startWin();
    }
    this.clock?.update();
  }
}
